import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { queryKeys } from '../../lib/queryKeys';

export interface OverdueInvoice {
  id: string;
  company_id: string;
  invoice_id_external: string;
  doc_number: string | null;
  customer_name: string;
  customer_id_external: string | null;
  invoice_date: string;
  due_date: string | null;
  total_amount: number;
  balance_due: number;
  currency: string;
  status: string;
  source: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OverdueInvoicesSummary {
  totalOverdue: number;
  invoiceCount: number;
  invoices: OverdueInvoice[];
  currency: string;
  averageDaysOverdue: number;
}

async function fetchOverdueInvoices(
  companyId: string
): Promise<OverdueInvoicesSummary> {
  const { data, error: fetchError } = await supabase
    .from('invoices')
    .select('*')
    .eq('company_id', companyId)
    .eq('status', 'overdue')
    .order('due_date', { ascending: true });

  if (fetchError) throw fetchError;

  const invoices = data || [];
  const totalOverdue = invoices.reduce((sum, invoice) => sum + (invoice.balance_due || 0), 0);

  // Calculate days overdue manually
  const today = new Date();
  const averageDaysOverdue =
    invoices.length > 0
      ? invoices.reduce((sum, invoice) => {
          if (invoice.due_date) {
            const dueDate = new Date(invoice.due_date);
            const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
            return sum + Math.max(0, daysOverdue);
          }
          return sum;
        }, 0) / invoices.length
      : 0;

  return {
    totalOverdue,
    invoiceCount: invoices.length,
    invoices,
    currency: invoices[0]?.currency || 'USD',
    averageDaysOverdue: Math.round(averageDaysOverdue),
  };
}

export function useOverdueInvoices(companyId: string | undefined) {
  const query = useQuery({
    queryKey: queryKeys.invoices.overdue(companyId || ''),
    queryFn: () => fetchOverdueInvoices(companyId!),
    enabled: !!companyId,
  });

  return {
    summary: query.data,
    isLoading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
}
